import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Table, Tag, Button, Input, Select, Empty, Popconfirm } from 'antd';
import { CodeSandboxOutlined, StopOutlined, PlayCircleOutlined, CopyOutlined } from '@ant-design/icons';
import { DeploymentWithContext, ArtifactRecipeSource } from '@src/api/deployment/deploymentApi';
import deploymentApi from '@src/api/deployment/deploymentApi';
import useTranslation from '@hooks/useTranslation';
import useKemuNotification from '@common/hooks/useKemuNotification';
import { ReactComponent as ServerIconSvg } from '@src/assets/img/interface/server.svg';
import styles from './ManageEnvironmentsModal.module.css';

const { Search } = Input;

/**
 * Deployments tab component displaying all deployments in a table with filtering
 */
const DeploymentsTab: React.FC = () => {
	const t = useTranslation('ManageEnvironmentsModal');
	const tCommon = t.withBaseKey('CommonWords');
	const showNotification = useKemuNotification();

	const [deployments, setDeployments] = useState<DeploymentWithContext[]>([]);
	const [loading, setLoading] = useState(false);
	const [searchText, setSearchText] = useState('');
	const [environmentFilter, setEnvironmentFilter] = useState<string>('all');
	const [recipeFilter, setRecipeFilter] = useState<string>('all');
	const [statusFilter, setStatusFilter] = useState<string>('all');

	/**
	 * Loads all deployments from the API
	 */
	const loadDeployments = useCallback(async () => {
		setLoading(true);
		try {
			const data = await deploymentApi.listAllDeployments();
			setDeployments(data);
		} catch (err) {
			console.error('Failed to load deployments:', err);
			showNotification({
				type: 'error',
				message: t('Deployments.Error.LoadFailed'),
			});
		} finally {
			setLoading(false);
		}
	}, [t, showNotification]);

	/**
	 * Handles activating a deployment
	 * @param deployment - Deployment to activate
	 */
	const handleActivateDeployment = useCallback(async (deployment: DeploymentWithContext) => {
		try {
			await deploymentApi.updateDeployment({
				deploymentId: deployment.id,
				deploymentInfo: {
					status: 'active',
				},
			});

			showNotification({
				type: 'success',
				message: t('Deployments.Activate.Success'),
			});
			// Reload deployments to get updated status
			loadDeployments();
		} catch (err) {
			console.error('Failed to activate deployment:', err);
			showNotification({
				type: 'error',
				message: t('Deployments.Activate.Error'),
			});
		}
	}, [showNotification, t, loadDeployments]);

	/**
	 * Handles deactivating a deployment
	 * @param deployment - Deployment to deactivate
	 */
	const handleDeactivateDeployment = useCallback(async (deployment: DeploymentWithContext) => {
		try {
			await deploymentApi.updateDeployment({
				deploymentId: deployment.id,
				deploymentInfo: {
					status: 'inactive',
				},
			});

			showNotification({
				type: 'success',
				message: t('Deployments.Deactivate.Success'),
			});
			// Reload deployments to get updated status
			loadDeployments();
		} catch (err) {
			console.error('Failed to deactivate deployment:', err);
			showNotification({
				type: 'error',
				message: t('Deployments.Deactivate.Error'),
			});
		}
	}, [showNotification, t, loadDeployments]);

	/**
	 * Handles copying deployment ID to clipboard
	 * @param deploymentId - Full deployment ID to copy
	 */
	const handleCopyDeploymentId = useCallback(async (deploymentId: string) => {
		try {
			await navigator.clipboard.writeText(deploymentId);
			showNotification({
				type: 'success',
				message: t('Deployments.CopyId.Success'),
			});
		} catch (err) {
			console.error('Failed to copy deployment ID:', err);
			showNotification({
				type: 'error',
				message: t('Deployments.CopyId.Error'),
			});
		}
	}, [showNotification, t]);

	/**
	 * Gets deployment status display info
	 * @param deployment - Deployment to get status for
	 * @returns status text and color
	 */
	const getDeploymentStatus = (deployment: DeploymentWithContext): { text: string; color: string } => {
		if (deployment.status === 'superseded') {
			return { text: deployment.status.toUpperCase(), color: 'gray' };
		}
		if (deployment.status === 'active') {
			return { text: deployment.status.toUpperCase(), color: 'green' };
		}
		if (deployment.status === 'failed') {
			return { text: deployment.status.toUpperCase(), color: 'red' };
		}
		return { text: deployment.status.toUpperCase(), color: 'default' };
	};

	/**
	 * Gets unique environments from deployments
	 */
	const uniqueEnvironments = useMemo(() => {
		const envMap = new Map<string, { id: string; name: string }>();
		deployments.forEach(d => {
			if (!envMap.has(d.environmentId)) {
				envMap.set(d.environmentId, {
					id: d.environmentId,
					name: d.environmentName,
				});
			}
		});
		return Array.from(envMap.values());
	}, [deployments]);

	/**
	 * Gets unique recipes from deployments
	 */
	const uniqueRecipes = useMemo(() => {
		const recipeMap = new Map<string, string>();
		deployments.forEach(d => {
			const recipeName = d.recipeSource.name;
			if (!recipeMap.has(recipeName)) {
				recipeMap.set(recipeName, recipeName);
			}
		});
		return Array.from(recipeMap.values()).sort();
	}, [deployments]);

	/**
	 * Filters deployments based on search text and filters
	 */
	const filteredDeployments = useMemo(() => {
		return deployments.filter(deployment => {
			// Search filter
			if (searchText) {
				const searchLower = searchText.toLowerCase();
				const matchesSearch = (
					deployment.recipeSource.name.toLowerCase().includes(searchLower) ||
					deployment.environmentName.toLowerCase().includes(searchLower) ||
					deployment.hostName.toLowerCase().includes(searchLower) ||
					(deployment.hostIpAddress && deployment.hostIpAddress.toLowerCase().includes(searchLower))
				);
				if (!matchesSearch) return false;
			}

			// Environment filter
			if (environmentFilter !== 'all') {
				if (environmentFilter !== deployment.environmentId) return false;
			}

			// Recipe filter
			if (recipeFilter !== 'all') {
				if (recipeFilter !== deployment.recipeSource.name) return false;
			}

			// Status filter
			if (statusFilter !== 'all') {
				const status = getDeploymentStatus(deployment);
				if (statusFilter === 'active' && status.text !== 'ACTIVE') return false;
				if (statusFilter === 'inactive' && status.text !== 'INACTIVE') return false;
				if (statusFilter === 'failed' && status.text !== 'FAILED') return false;
			}

		return true;
	}).sort((a, b) => {
		// Sort by updatedAt date in descending order (most recently updated first)
		const dateA = new Date(a.updatedAt).getTime();
		const dateB = new Date(b.updatedAt).getTime();
		if(a.status === 'active' && b.status !== 'active') return -1;
		return dateB - dateA;
	});
	}, [deployments, searchText, environmentFilter, recipeFilter, statusFilter]);

	// Load deployments when component mounts
	useEffect(() => {
		loadDeployments();
	}, [loadDeployments]);

	/**
	 * Table columns definition
	 */
	const columns = [
		{
			title: tCommon('DeploymentId'),
			key: 'deploymentId',
			width: 150,
			render: (_: unknown, record: DeploymentWithContext) => {
				const last5Chars = record.id.slice(-5);
				return (
					<div className={styles.DeploymentTableId}>
						<span className={styles.DeploymentTableIdText}>...{last5Chars}</span>
						<Button
							type="text"
							size="small"
							icon={<CopyOutlined />}
							onClick={() => handleCopyDeploymentId(record.id)}
							className={styles.DeploymentTableCopyButton}
							title={t('Deployments.CopyId')}
						/>
					</div>
				);
			},
		},
		{
			title: tCommon('Recipe'),
			key: 'recipe',
			render: (_: unknown, record: DeploymentWithContext) => (
				<div className={styles.DeploymentTableRecipe}>
					<CodeSandboxOutlined className={styles.DeploymentTableIcon} />
					<span>{record.recipeSource.name}</span>
				</div>
			),
		},
		{
			title: tCommon('Version'),
			width: 100,
			key: 'version',
			render: (_: unknown, record: DeploymentWithContext) => {
				const version = (record.recipeSource as ArtifactRecipeSource)?.version;
				return version ? <Tag>{`v${version}`}</Tag> : <Tag>N/A</Tag>;
			},
		},
		{
			title: tCommon('Environment'),
			key: 'environment',
			dataIndex: 'environmentName',
		},
		{
			title: tCommon('Host'),
			key: 'host',
			render: (_: unknown, record: DeploymentWithContext) => (
				<div className={styles.DeploymentTableHost}>
					<ServerIconSvg className={styles.DeploymentTableIcon} />
					<div className={styles.DeploymentTableHostInfo}>
						<span>{record.hostName}</span>
						{record.hostIpAddress && (
							<span className={styles.DeploymentTableHostIp}>{record.hostIpAddress}</span>
						)}
					</div>
				</div>
			),
		},
		{
			title: tCommon('Status'),
			width: 130,
			key: 'status',
			render: (_: unknown, record: DeploymentWithContext) => {
				const status = getDeploymentStatus(record);
				return <Tag color={status.color}>{status.text}</Tag>;
			},
		},
		{
			title: tCommon('Replicas'),
			key: 'replicas',
			width: 100,
			render: (_: unknown, record: DeploymentWithContext) => {
				const running = record.workers?.filter(w => w.state === 'running').length || 0;
				const desired = record.desiredWorkerCount || 0;
				const color = running === desired ? 'green' : running < desired ? 'orange' : 'default';
				return (
					<Tag color={color}>
						{running} / {desired}
					</Tag>
				);
			},
		},
		{
			title: tCommon('Actions'),
			key: 'actions',
			width: 130,
			render: (_: unknown, record: DeploymentWithContext) => {
				const status = getDeploymentStatus(record);
				const isActive = status.text === 'ACTIVE';

				if (isActive) {
					return (
						<Popconfirm
							title={t('DeactivateConfirm.Title')}
							description={t('DeactivateConfirm.Description', { name: record.recipeSource.name })}
							onConfirm={() => handleDeactivateDeployment(record)}
							okText={t('DeactivateConfirm.Confirm')}
							cancelText={t('DeactivateConfirm.Cancel')}
							okButtonProps={{ danger: true }}
						>
							<Button
								type="text"
								size="small"
								icon={<StopOutlined />}
								danger
							>
								{t('Deployments.Stop')}
							</Button>
						</Popconfirm>
					);
				} else if(status.text === 'INACTIVE') {
					return (
						<Button
							type="text"
							size="small"
							icon={<PlayCircleOutlined />}
							onClick={() => handleActivateDeployment(record)}
						>
							{t('Deployments.Start')}
						</Button>
					);
				}
			},
		},
	];

	return (
		<>
			<div className={styles.DeploymentsFiltersContainer}>
				<Search
					placeholder={t('Deployments.SearchPlaceholder')}
					value={searchText}
					onChange={(e) => setSearchText(e.target.value)}
					allowClear
					className={styles.DeploymentsSearch}
				/>
				<Select
					value={environmentFilter}
					onChange={setEnvironmentFilter}
					className={styles.DeploymentsFilter}
					placeholder={tCommon('Environment')}
					popupClassName={styles.SelectDropdown}
				>
					<Select.Option value="all">{tCommon('AllEnvironments')}</Select.Option>
					{uniqueEnvironments.map(env => (
						<Select.Option key={env.id} value={env.id}>
							{env.name}
						</Select.Option>
					))}
				</Select>
				<Select
					value={recipeFilter}
					onChange={setRecipeFilter}
					className={styles.DeploymentsFilter}
					placeholder={tCommon('Recipe')}
					popupClassName={styles.SelectDropdown}
				>
					<Select.Option value="all">{tCommon('AllRecipes')}</Select.Option>
					{uniqueRecipes.map(recipe => (
						<Select.Option key={recipe} value={recipe}>
							{recipe}
						</Select.Option>
					))}
				</Select>
				<Select
					value={statusFilter}
					onChange={setStatusFilter}
					className={styles.DeploymentsFilter}
					placeholder={tCommon('Status')}
					popupClassName={styles.SelectDropdown}
				>
					<Select.Option value="all">{tCommon('AllStatuses')}</Select.Option>
					<Select.Option value="active">{tCommon('Active')}</Select.Option>
					<Select.Option value="inactive">{tCommon('Inactive')}</Select.Option>
					<Select.Option value="failed">{tCommon('Failed')}</Select.Option>
				</Select>
			</div>
			<Table
				columns={columns}
				dataSource={filteredDeployments}
				loading={loading}
				rowKey="id"
				pagination={{
					pageSize: 10,
					showSizeChanger: true,
					showTotal: (total) => t('Deployments.Table.Total', { total }),
				}}
				locale={{
					emptyText: <Empty description={t('Deployments.NoDeployments')} />,
				}}
				scroll={{ y: 400 }}
			/>
		</>
	);
};

export default DeploymentsTab;
